import { useEffect, useState } from "react";
import { getJSON, putJSON } from "../services/api";
import { GroupGuard } from "../auth/GroupGuard";

const REQUIRED_GROUPS = [
  "bcba776b-ad66-4c3c-b253-a10a1b65c294", // Admins - TI
  "1c4b0acb-3657-48c1-9024-0904307a94b7", // CEP - Admins
];

const MAX_BICOS = 20;

const LABELS: Record<string, string> = {
  viola: "Violação (fora dos limites)",
  sobre: "Acima do alvo",
  sub: "Abaixo do alvo",
  drift: "Fora da faixa ideal",
  tol: "Em tolerância",
  vol: "Volume (meta)",
  volumeGapDias: "Gap de volume diário",
  mode: "Modo de cálculo do desvio",
  insideWeight: "Peso dentro da faixa",
  outsideWeight: "Peso fora da faixa",
  leiturasPorDia: "Meta de leituras por dia",
  maxBicos: "Máximo de bicos por operador",
  maxShare: "Máxima participação (%)",
  modo: "Multiplicador de dias",
};

const HELPS: Record<string, string> = {
  viola: "Penalidade aplicada quando a média do bico está fora dos limites [min, max].",
  sobre: "Peso aplicado ao % de leituras acima do alvo (superdosagem).",
  sub: "Peso aplicado ao % de leituras abaixo do alvo (subdosagem).",
  drift: "Penalidade quando a média está fora da faixa ideal, mas dentro dos limites.",
  tol: "Peso aplicado ao % de leituras dentro da tolerância.",
  vol: "Bônus ou penalidade proporcional ao volume de leituras (quanto mais, melhor).",
  volumeGapDias: "Penalidade se não atingir a meta de leituras diárias.",
  mode: "Forma de calcular o desvio médio absoluto (DMA).",
  insideWeight: "Peso do desvio quando a média está dentro da faixa ideal.",
  outsideWeight: "Peso do desvio quando a média está fora da faixa ideal.",
  leiturasPorDia: "Meta mínima de leituras/dia por bico. Se não atingir, aplica penalidade.",
  maxBicos: "Número máximo de bicos simultâneos atribuídos a um operador antes de gerar alerta.",
  maxShare: "Percentual máximo da participação de um operador no período (não pode passar de 100).",
  modo: "Define como considerar dias trabalhados no score: máximo, soma ou média.",
};

export default function ConfigAnalise() {
  const [cfg, setCfg] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getJSON("/config/analise").then(setCfg);
  }, []);

  if (!cfg) return <div className="p-4">Carregando configs...</div>;

  const handleChange = (secao: string, chave: string, value: any) => {
    setCfg((prev: any) => ({
      ...prev,
      [secao]: { ...prev[secao], [chave]: value },
    }));
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await putJSON("/config/analise", cfg);
      if (!res) throw new Error("Erro ao salvar");
      alert("✅ Configurações salvas com sucesso!");
    } catch (err) {
      console.error("Erro ao salvar:", err);
      alert("❌ Falha ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  const renderWarnings = (secao: string, chave: string, valor: any) => {
    const warnings: string[] = [];

    if (typeof valor === "number") {
      if (valor < 0) warnings.push("Valor não pode ser negativo.");
    }

    if (secao === "sobrecarga" && chave === "maxShare" && valor > 100) {
      warnings.push("Participação percentual não pode ser maior que 100%.");
    }

    if (secao === "sobrecarga" && chave === "maxBicos" && valor > MAX_BICOS) {
      warnings.push(
        `Máximo de bicos por operador não pode ser maior que ${MAX_BICOS}.`
      );
    }

    if (secao === "pesos") {
      if (chave === "sub" && valor < cfg.pesos.sobre) {
        warnings.push(
          "⚠️ Peso de subdosagem deveria ser ≥ ao de superdosagem (sub é multa, sobre é desperdício)."
        );
      }
    }

    return warnings.length ? (
      <ul className="text-xs text-red-600 list-disc list-inside">
        {warnings.map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ul>
    ) : null;
  };

  const renderInput = (secao: string, chave: string, valor: any) => {
    if (secao === "dma" && chave === "mode") {
      return (
        <select
          className="border rounded px-2 py-1"
          value={valor}
          onChange={(e) => handleChange(secao, chave, e.target.value)}
        >
          <option value="center">Centro</option>
          <option value="limit">Limite</option>
          <option value="mixed">Misto</option>
        </select>
      );
    }

    if (secao === "multiplicadorDias" && chave === "modo") {
      return (
        <select
          className="border rounded px-2 py-1"
          value={valor}
          onChange={(e) => handleChange(secao, chave, e.target.value)}
        >
          <option value="max">Máximo</option>
          <option value="sum">Soma</option>
          <option value="avg">Média</option>
        </select>
      );
    }

    return (
      <input
        type={typeof valor === "number" ? "number" : "text"}
        step="any"
        className="border rounded px-2 py-1"
        value={typeof valor === "number" ? valor : String(valor ?? "")}
        onChange={(e) =>
          handleChange(
            secao,
            chave,
            typeof valor === "number" ? Number(e.target.value) : e.target.value
          )
        }
      />
    );
  };

  return (
    <GroupGuard allowedGroups={REQUIRED_GROUPS}>
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold">⚙️ Configuração de Análise</h1>

      {Object.entries(cfg).map(([secao, valores]) => (
        <fieldset
          key={secao}
          className="border rounded-lg p-4 bg-white shadow-sm"
        >
          <legend className="text-lg font-semibold px-2 capitalize">
            {secao}
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
            {Object.entries(valores as any).map(([chave, valor]) => (
              <label key={chave} className="flex flex-col text-sm gap-1">
                <span className="font-medium text-slate-700">
                  {LABELS[chave] || chave}
                </span>
                {renderInput(secao, chave, valor)}
                <span className="text-xs text-slate-500">
                  {HELPS[chave] || ""}
                </span>
                {renderWarnings(secao, chave, valor)}
              </label>
            ))}
          </div>
        </fieldset>
      ))}

      <div className="pt-4">
        <button
          disabled={saving}
          onClick={saveConfig}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg shadow hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? "Salvando..." : "Salvar Configurações"}
        </button>
      </div>
    </div>
    </GroupGuard>
  );
}
